/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/dialProfileApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for Dial Profiles.
    Centralizes all API calls related to Dial Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListDialProfilesResponse,
  DialProfileResponse,
  DialProfilePayload,
} from '../types';

/**
 * Fetch list of dial profiles
 */
export async function fetchDialProfiles(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  active?: boolean;
  domainId?: number;
}): Promise<ListDialProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortDirection: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.active != null && { active: params.active.toString() }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/dial-profiles?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch dial profiles');
  }

  return await response.json();
}

/**
 * Fetch single dial profile by ID or UUID
 */
export async function fetchDialProfile(id: string | number): Promise<DialProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dial-profiles/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to fetch dial profile');
  }

  return await response.json();
}

/**
 * Create dial profile
 */
export async function createDialProfile(
  payload: DialProfilePayload
): Promise<DialProfileResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/dial-profiles'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to create dial profile');
  }

  return await response.json();
}

/**
 * Update dial profile
 */
export async function updateDialProfile(
  id: string | number,
  payload: DialProfilePayload
): Promise<DialProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dial-profiles/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to update dial profile');
  }

  return await response.json();
}

/**
 * Delete dial profile
 */
export async function deleteDialProfile(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dial-profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to delete dial profile');
  }
}

